/**
 * Document Chunk Service
 *
 * Wraps the Railway routes that ran as the `vectorize-document` Supabase
 * edge function. The vectorize action also writes a row to `log_vectors`
 * server-side — no client-side log call is needed (the prior service's
 * `logVectorizationAttempt` is retired here).
 */

import type { DeleteResult, ListResult, ServiceContext } from "@prolarus/shared";
import { ApiError, apiDelete, apiGet, apiPost } from "@/lib/apiClient";

export interface DocumentChunk {
  id: string;
  company_id: string;
  source_entity_type: string;
  source_entity_id: string;
  attachment_id: string;
  chunk_index: number;
  chunk_text: string;
  chunk_metadata: {
    page_number: number;
    section_header: string;
    chunk_type: string;
  };
  created: string;
  created_by: string | null;
}

export interface VectorizeResult {
  data: { chunks_created: number; page_count: number; sections_extracted: number } | null;
  error: Error | null;
}

export interface VectorizationLog {
  id: string;
  company_id: string;
  attachment_id: string;
  source_entity_type: string;
  source_entity_id: string;
  status: "success" | "error";
  error_message: string | null;
  chunks_created: number | null;
  page_count: number | null;
  sections_extracted: number | null;
  processing_time_ms: number | null;
  triggered_by: string | null;
  created: string;
  triggered_by_profile?: { full_name: string | null } | null;
}

interface VectorizeServerResponse {
  success: boolean;
  chunks_created?: number;
  page_count?: number;
  sections_extracted?: number;
  processing_time_ms?: number;
  error?: string;
}

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

export const documentChunkService = {
  async getChunkCountsByAttachments(
    attachmentIds: string[],
  ): Promise<{ data: Record<string, number>; error: Error | null }> {
    if (attachmentIds.length === 0) return { data: {}, error: null };
    try {
      const result = await apiPost<{ data: Record<string, number> }>(
        "/api/document-chunks/counts",
        { attachment_ids: attachmentIds },
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: {}, error: toError(err) };
    }
  },

  async hasVectorizedDocuments(
    sourceEntityType: string,
    sourceEntityId: string,
  ): Promise<{ hasChunks: boolean; error: Error | null }> {
    try {
      const result = await apiGet<{ hasChunks: boolean }>(
        `/api/document-chunks/has-vectorized?sourceEntityType=${encodeURIComponent(sourceEntityType)}&sourceEntityId=${encodeURIComponent(sourceEntityId)}`,
      );
      return { hasChunks: result.hasChunks, error: null };
    } catch (err) {
      return { hasChunks: false, error: toError(err) };
    }
  },

  async listByAttachment(attachmentId: string): Promise<ListResult<DocumentChunk>> {
    try {
      const result = await apiGet<{ data: DocumentChunk[] }>(
        `/api/document-chunks/by-attachment/${encodeURIComponent(attachmentId)}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  async listLogsByAttachment(attachmentId: string): Promise<ListResult<VectorizationLog>> {
    try {
      const result = await apiGet<{ data: VectorizationLog[] }>(
        `/api/vectorize/logs/by-attachment/${encodeURIComponent(attachmentId)}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  /**
   * Trigger vectorization. The server logs success/error to log_vectors
   * directly — no client-side logging step needed. context is accepted for
   * backward-compat with the prior service signature; company_id is
   * derived from the JWT server-side.
   */
  async vectorizeDocument(
    attachmentId: string,
    sourceEntityType: string,
    sourceEntityId: string,
    _context: ServiceContext,
  ): Promise<VectorizeResult> {
    try {
      const data = await apiPost<VectorizeServerResponse>("/api/vectorize", {
        attachment_id: attachmentId,
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
      });
      if (!data.success) {
        return { data: null, error: new Error(data.error ?? "Vectorization failed") };
      }
      return {
        data: {
          chunks_created: data.chunks_created ?? 0,
          page_count: data.page_count ?? 0,
          sections_extracted: data.sections_extracted ?? 0,
        },
        error: null,
      };
    } catch (err) {
      return { data: null, error: toError(err) };
    }
  },

  async deleteByAttachment(
    attachmentId: string,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    try {
      const result = await apiDelete<{ success: boolean; changesLogged?: boolean }>(
        `/api/document-chunks/by-attachment/${encodeURIComponent(attachmentId)}`,
      );
      return { success: result.success, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      return { success: false, error: toError(err), changesLogged: false };
    }
  },
};
